import { useCallback, useEffect, useMemo, useState } from "react";
import { SERVER_URL } from "../../../../utils/serverConfig";

const ALL = {
  pulse: true, whatWentInto: true, aiAdoption: true, acrossTheDay: true,
  aiBrief: true, timeByApp: true, hourlyTab: true,
};
const keep = (ids) => {
  const out = { ...ALL };
  Object.keys(out).forEach((k) => { out[k] = ids.includes(k); });
  return out;
};

export const WS_PRESETS = [
  { id: "member-dossier", title: "Per-Member Deep-Dive Dossier", audience: "Manager / people review", blurb: "The Work Summary you already use — what they did, apps & AI, websites, workstreams, daily rhythm.", isDefault: true },
  { id: "ceo-brief", title: "CEO / Executive Brief", audience: "Founders / CEOs", blurb: "Decision-first — KPIs, what needs attention, key findings, AI adoption, red flags." },
  { id: "workforce-full", title: "Full Workforce Activity Report", audience: "Whole-org review", blurb: "Full day on one person — rhythm, work, tools, AI, flags, time by app." },
  { id: "client-practice", title: "Per-Client / Practice Report", audience: "Agencies / practices", blurb: "Hours and work per client or project — who touched what." },
  { id: "accounts-review", title: "Accounts & Finance Review", audience: "Accounts / finance", blurb: "Work by area, document load, day-by-day log." },
  { id: "websites-apps", title: "Websites & Applications Report", audience: "Site / app audit", blurb: "Every website and app they used, with hours." },
  { id: "daily-weekly", title: "Daily / Weekly Activity Report", audience: "Ongoing check-ins", blurb: "Short-window activity plus the hour-by-hour work log." },
  { id: "idle-review", title: "Idle Time & Reasons Review", audience: "Utilisation / capacity", blurb: "Active vs away, longest run, hour grid." },
  { id: "red-flags", title: "Red Flags / Conduct Review", audience: "Compliance / HR", blurb: "What stood out — risks and the pulse around them." },
  { id: "org-intel", title: "Organisation Intelligence Report", audience: "What's stuck", blurb: "On this person: shape of the day, blockers, written brief." },
];

export const WS_PRESET_SECTIONS = {
  "member-dossier": { ...ALL },
  "workforce-full": { ...ALL },
  "daily-weekly": { ...ALL },
  "ceo-brief": keep(["pulse", "aiBrief", "aiAdoption"]),
  "client-practice": keep(["pulse", "acrossTheDay", "timeByApp"]),
  "accounts-review": keep(["whatWentInto", "acrossTheDay", "timeByApp", "hourlyTab"]),
  "websites-apps": keep(["whatWentInto", "timeByApp", "hourlyTab"]),
  "idle-review": keep(["pulse", "hourlyTab"]),
  "red-flags": keep(["pulse", "aiBrief"]),
  "org-intel": keep(["pulse", "whatWentInto", "aiBrief"]),
};

export const DEFAULT_WS_PRESET = "member-dossier";

export function emptyWsReport() {
  return { preset: DEFAULT_WS_PRESET, sections: { ...ALL } };
}

export function resolveWsReport(cfg) {
  const id = WS_PRESET_SECTIONS[cfg?.preset] ? cfg.preset : DEFAULT_WS_PRESET;
  return { preset: id, sections: { ...WS_PRESET_SECTIONS[id] } };
}

export function wsShow(resolved, id) {
  return resolved?.sections?.[id] !== false;
}

export function useWorkSummaryReport() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    let dead = false;
    const token = typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
    fetch(`${SERVER_URL}/api/v1/organizations/work-summary-report`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .then((j) => { if (!dead) setCfg(j?.data?.report || emptyWsReport()); })
      .catch(() => { if (!dead) setCfg(emptyWsReport()); });
    return () => { dead = true; };
  }, []);
  const resolved = useMemo(() => resolveWsReport(cfg), [cfg]);
  const show = useCallback((id) => wsShow(resolved, id), [resolved]);
  return { ready: !!cfg, resolved, show };
}
